import { useEffect, useState } from 'react';
import { api } from '../../api';
import type { LivroRunas, Runa } from '../../api/types';
import { ApiError } from '../../api/types';
import { existe, t } from '../../i18n/idioma';
import type { MeuPortalRuna, RunaRitualView } from '../../world/useWorld';
import { IcCasa, IcMonstro } from '../icones/IconesV3';
import './runas.css';

/**
 * Teleportar › Runas (techspec-livro-de-runas): o livro de runas da conta. "Gravar runa" conjura Mark onde o personagem
 * está; clicar numa runa faz o recall; o portal (azul com guarda, vermelho sem guarda) leva os outros junto. A caveira
 * marca lugar sem guarda e o escudo verde, runa gravada na área protegida (sem PvP). Minha casa fica fixa no topo.
 * Nome, cor, ordem e apagar vão pelo HTTP; gravar, recall e portal, pelo hub (quem chama decide).
 */
export const CORES_RUNA = ['#e2b33c', '#d0503e', '#4f86f0', '#56b86a', '#9c6ad8', '#9a958a'] as const;

export function IcRuna({ cor, className }: { cor: string; className?: string }) {
  return (
    <svg className={`rb-ic ${className ?? ''}`} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 4.5 15.5 2.8 19.5 8 18 19.5 8.5 21.2 4.5 16z" fill="#2a2620" stroke="#0d0b08" strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M6.6 5.4 15.2 3.9 18.5 8.3 17.1 18.6 8.8 20.1 5.4 15.8z" fill="#4a443a" />
      <path d="M12 7v10M9 9.5l3 2.5 3-2.5M9.5 15l2.5-3 2.5 3" fill="none" stroke={cor} strokeWidth="4" strokeLinecap="round" opacity=".18" />
      <path d="M12 7v10M9 9.5l3 2.5 3-2.5M9.5 15l2.5-3 2.5 3" fill="none" stroke={cor} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** O portal do UO: azul para lugar com guarda, vermelho para lugar sem guarda. */
function IcPortal({ vermelho }: { vermelho: boolean }) {
  const id = vermelho ? 'rb-pv' : 'rb-pa';
  return (
    <svg className="rb-pt" viewBox="0 0 24 24" aria-hidden="true">
      <defs>
        <radialGradient id={id} cx=".5" cy=".5" r=".55">
          <stop offset="0" stopColor="#fff" />
          <stop offset=".35" stopColor={vermelho ? '#f2b0a4' : '#c8dcff'} />
          <stop offset="1" stopColor={vermelho ? '#8a1c14' : '#1c2a86'} />
        </radialGradient>
      </defs>
      <ellipse cx="12" cy="12" rx="6.2" ry="10" fill={`url(#${id})`} />
    </svg>
  );
}

function IcEscudo({ title }: { title: string }) {
  return (
    <svg className="rb-esc" viewBox="0 0 24 24" role="img">
      <title>{title}</title>
      <path d="M12 2 4 5v6c0 5 3.4 9.4 8 11 4.6-1.6 8-6 8-11V5l-8-3z" />
    </svg>
  );
}

const nomeDoLugar = (mapa: string, nome: string) => (existe(`portal.destino.${mapa}`) ? t(`portal.destino.${mapa}`) : nome || mapa);

/** O selo do lugar: escudo verde (área protegida) ou caveira (sem guarda). */
function Selo({ protegida, vermelho }: { protegida: boolean; vermelho: boolean }) {
  if (protegida) return <IcEscudo title={t('runa.protegida')} />;
  if (vermelho) return <IcMonstro className="vj-pk" title={t('hud.viajar.semGuarda')} />;
  return null;
}

export function RunasPainel({
  mapaNome, cacando, coletando, ritual, meuPortal, runasSeq, onMarcar, onRecall, onPortal
}: {
  /** Onde o personagem está agora (o nome da sala). */
  mapaNome: string;
  cacando: boolean;
  coletando: boolean;
  ritual: RunaRitualView | null;
  /** O portal que este personagem abriu e ainda está no chão. */
  meuPortal: MeuPortalRuna | null;
  runasSeq: number;
  onMarcar(nome: string | null, cor: number, runaId: string | null): void;
  onRecall(id: string): void;
  onPortal(id: string): void;
}) {
  const [livro, setLivro] = useState<LivroRunas | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [gravando, setGravando] = useState(false);
  const [nome, setNome] = useState('');
  const [cor, setCor] = useState(0);
  const [editando, setEditando] = useState<string | null>(null);
  const [apagar, setApagar] = useState(false);
  const [agora, setAgora] = useState(() => performance.now());

  useEffect(() => {
    let vivo = true;
    void api.runas()
      .then(l => { if (vivo) { setLivro(l); setErro(null); } })
      .catch(e => { if (vivo) setErro(e instanceof ApiError ? e.message : t('runa.erro')); });
    return () => { vivo = false; };
  }, [runasSeq]);

  // A barra da conjuração e a contagem do portal andam sozinhas.
  useEffect(() => {
    if (!ritual && !meuPortal) return;
    const i = window.setInterval(() => setAgora(performance.now()), 100);
    return () => window.clearInterval(i);
  }, [ritual, meuPortal]);

  function aplicar(p: Promise<LivroRunas>) {
    void p.then(l => { setLivro(l); setErro(null); })
      .catch(e => setErro(e instanceof ApiError ? e.message : t('runa.erro')));
  }

  if (!livro) return <p className="vj-nota">{erro ?? t('comum.carregando')}</p>;
  if (!livro.ativo) return <p className="vj-nota">{t('runa.desligado')}</p>;

  const cheio = livro.runas.length >= livro.vagas;
  const conjurando = !!ritual;
  const travado = cacando || coletando || conjurando;
  const pct = ritual && ritual.ms > 0 ? Math.min(100, ((agora - ritual.inicio) / ritual.ms) * 100) : 0;
  const restante = meuPortal ? Math.max(0, Math.ceil((meuPortal.ate - agora) / 1000)) : 0;
  const portalVivo = !!meuPortal && restante > 0;
  const marcando = ritual?.tipo === 'mark';

  function gravar() {
    setGravando(false);
    onMarcar(nome.trim() || null, cor, null);
  }

  const botaoPortal = (id: string, vermelho: boolean, off: boolean) => livro.portal && (
    <button type="button" className={`rb-abre${vermelho ? ' is-v' : ''}`} aria-disabled={off || undefined}
      title={t(vermelho ? 'runa.portalVermelho' : 'runa.portal')} aria-label={t(vermelho ? 'runa.portalVermelho' : 'runa.portal')}
      onClick={off ? undefined : () => onPortal(id)}>
      <IcPortal vermelho={vermelho} />
    </button>
  );

  const estadoDaLinha = (id: string, padrao: string) => {
    if (ritual?.tipo === 'portal' && ritual.alvo === id)
      return <><i className="rb-pal">Vas Rel Por</i> · {t('runa.abrindo')}</>;
    if (ritual?.tipo === 'recall' && ritual.alvo === id)
      return <><i className="rb-pal">Kal Ort Por</i> · {t('runa.viajando')}</>;
    if (portalVivo && meuPortal!.alvo === id) return <b className="rb-seg">{t('runa.portalAberto', { s: restante })}</b>;
    return padrao;
  };
  const corDaLinha = (id: string, vermelho: boolean) =>
    (ritual?.tipo === 'portal' && ritual.alvo === id) || (portalVivo && meuPortal!.alvo === id) ? (vermelho ? ' is-pv' : ' is-pa') : '';

  return (
    <div className="rb">
      {marcando ? (
        <div className="rb-marca is-conj">
          <span className="rb-marca__ic"><IcRuna cor="#f2e8d5" /></span>
          <span className="rb-marca__t">
            <b>{t('runa.gravando')}</b>
            <small><i className="rb-pal">Kal Por Ylem</i> · {t('runa.naoAnde')}</small>
            <span className="rb-barra"><i style={{ width: `${pct}%` }} /></span>
          </span>
        </div>
      ) : (
        <div className="rb-marca">
          <span className="rb-marca__ic"><IcRuna cor="#f2e8d5" /></span>
          <span className="rb-marca__t">
            <b>{t('runa.voceEsta')}</b>
            <small>{mapaNome}</small>
          </span>
          <button type="button" className="rb-btn" disabled={cheio || gravando || conjurando || coletando}
            onClick={() => { setNome(''); setGravando(true); }}>
            {cheio ? t('runa.livroCheio') : t('runa.marcar')}
          </button>
        </div>
      )}

      {gravando && (
        <div className="rb-form">
          <label className="rb-lbl" htmlFor="rb-nome">{t('runa.nome')}</label>
          <input id="rb-nome" className="rb-in" maxLength={24} value={nome} placeholder={mapaNome}
            onChange={e => setNome(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') gravar(); }} autoFocus />
          <div className="rb-cores" role="radiogroup" aria-label={t('runa.cores')}>
            {CORES_RUNA.map((h, i) => (
              <button key={h} type="button" role="radio" aria-checked={cor === i} title={t(`runa.cor.${i}`)} aria-label={t(`runa.cor.${i}`)}
                className="rb-cor" style={{ ['--c' as string]: h }} onClick={() => setCor(i)} />
            ))}
            <small className="rb-cont">{nome.length}/24</small>
          </div>
          <div className="rb-acoes">
            <button type="button" className="rb-btn is-pri" onClick={gravar}>{t('runa.gravar')}</button>
            <button type="button" className="rb-btn is-sec" onClick={() => setGravando(false)}>{t('comum.cancelar')}</button>
          </div>
          <small className="rb-dica">{t('runa.dicaMark')}</small>
        </div>
      )}

      {cacando && <p className="vj-nota is-aviso">{t('runa.cacando')}</p>}
      {erro && <p className="vj-nota is-aviso" role="alert">{erro}</p>}

      <div className="rb-lista">
        {livro.casa && (
          <div className={`rb-r is-casa${corDaLinha('casa', livro.casa.vermelho)}`}>
            <button type="button" className="rb-r__go" aria-disabled={travado || undefined}
              title={t('runa.recall', { nome: livro.casa.rotulo || t('hud.viajar.minhaCasa') })}
              onClick={travado ? undefined : () => onRecall('casa')}>
              <span className="rb-r__ic"><IcCasa /></span>
              <span className="rb-r__n">
                <b>{livro.casa.rotulo || t('hud.viajar.minhaCasa')}</b>
                <small>{estadoDaLinha('casa', t('runa.casaFixa', { lugar: nomeDoLugar(livro.casa.mapa, livro.casa.mapaNome) }))}</small>
                {ritual && ritual.tipo !== 'mark' && ritual.alvo === 'casa' && <span className="rb-barra"><i style={{ width: `${pct}%` }} /></span>}
              </span>
              <span className="rb-r__m"><Selo protegida={false} vermelho={livro.casa.vermelho} /></span>
            </button>
            {botaoPortal('casa', livro.casa.vermelho, travado || portalVivo)}
          </div>
        )}

        {livro.runas.map((r: Runa, i) => {
          const off = travado || r.riscada;
          const aberta = editando === r.id;
          const lugar = nomeDoLugar(r.mapa, r.mapaNome);
          return (
            <div key={r.id} className={`rb-item${aberta ? ' is-aberta' : ''}`}>
              <div className={`rb-r${r.riscada ? ' is-sumiu' : ''}${corDaLinha(r.id, r.vermelho)}`}>
                <button type="button" className="rb-r__go" aria-disabled={off || undefined}
                  title={r.riscada ? t('runa.riscada') : t('runa.recall', { nome: r.nome })}
                  onClick={off ? undefined : () => onRecall(r.id)}>
                  <span className="rb-r__ic"><IcRuna cor={r.riscada ? '#5a554c' : CORES_RUNA[r.cor] ?? CORES_RUNA[0]} /></span>
                  <span className="rb-r__n">
                    <b>{r.nome}</b>
                    <small>{r.riscada ? t('runa.riscada') : estadoDaLinha(r.id, t('runa.lugarPor', { lugar, nome: r.marcadaPor }))}</small>
                    {ritual && ritual.tipo !== 'mark' && ritual.alvo === r.id && <span className="rb-barra"><i style={{ width: `${pct}%` }} /></span>}
                  </span>
                  <span className="rb-r__m">{!r.riscada && <Selo protegida={r.protegida} vermelho={r.vermelho} />}</span>
                </button>
                {botaoPortal(r.id, r.vermelho, off || portalVivo)}
                <button type="button" className="rb-mais" aria-expanded={aberta} title={t('runa.editar')} aria-label={t('runa.editar')}
                  onClick={() => { setEditando(aberta ? null : r.id); setApagar(false); }}>⋯</button>
              </div>
              {aberta && (
                <div className="rb-ed">
                  <input className="rb-in" maxLength={24} defaultValue={r.nome} aria-label={t('runa.nome')}
                    onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                    onBlur={e => {
                      const v = e.target.value.trim();
                      if (v && v !== r.nome) aplicar(api.runaEditar(r.id, { nome: v }));
                    }} />
                  <div className="rb-cores" role="radiogroup" aria-label={t('runa.cores')}>
                    {CORES_RUNA.map((h, c) => (
                      <button key={h} type="button" role="radio" aria-checked={r.cor === c} title={t(`runa.cor.${c}`)} aria-label={t(`runa.cor.${c}`)}
                        className="rb-cor" style={{ ['--c' as string]: h }}
                        onClick={() => { if (r.cor !== c) aplicar(api.runaEditar(r.id, { cor: c })); }} />
                    ))}
                  </div>
                  <div className="rb-ed__b">
                    <button type="button" className="rb-mini" disabled={i === 0} title={t('runa.subir')} aria-label={t('runa.subir')}
                      onClick={() => aplicar(api.runaEditar(r.id, { mover: -1 }))}>▲</button>
                    <button type="button" className="rb-mini" disabled={i === livro.runas.length - 1} title={t('runa.descer')} aria-label={t('runa.descer')}
                      onClick={() => aplicar(api.runaEditar(r.id, { mover: 1 }))}>▼</button>
                    <button type="button" className="rb-mini" disabled={conjurando || coletando}
                      onClick={() => { setEditando(null); onMarcar(null, r.cor, r.id); }}>{t('runa.remarcar')}</button>
                    <button type="button" className={`rb-mini is-del${apagar ? ' is-conf' : ''}`}
                      onClick={() => {
                        if (!apagar) { setApagar(true); return; }
                        setEditando(null);
                        aplicar(api.runaApagar(r.id));
                      }}>
                      {apagar ? t('runa.apagarConfirma') : t('runa.apagar')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {livro.runas.length === 0 && <p className="vj-nota">{t('runa.vazio')}</p>}
        {!cheio && (
          <div className="rb-vaga">
            <span>{t('runa.vagas', { n: livro.vagas - livro.runas.length })}</span>
            <small>{livro.runas.length}/{livro.vagas}</small>
          </div>
        )}
      </div>
      {cheio && (
        <p className="vj-nota">
          {t('runa.cheioNota', { n: livro.vagas })}
          {!livro.vip && livro.vagasComVip > livro.vagas && <> {t('runa.cheioVip', { n: livro.vagasComVip - livro.vagas })}</>}
        </p>
      )}
    </div>
  );
}
